// src/components/branch/ViewModal.tsx
import React, { memo } from 'react';
import { Modal } from '../UI/Modal';
import { Order } from '../../types/types';
import { X, ArrowRight } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  t: (key: string, params?: any) => string;
  isRtl: boolean;
}

const ViewModal: React.FC<Props> = memo(({ isOpen, onClose, order, t, isRtl }) => {
  if (!order) return null;

  const isDelivered = order.status === 'delivered';
  const originalTotal = order.total || 0;
  const adjustedTotal = isDelivered ? (order.adjustedTotal ?? originalTotal) : originalTotal;
  const receivedTotal = isDelivered
    ? order.items.reduce((sum, item) => {
        if (item.rejected) return sum;
        return sum + (item.receivedQuantity || 0) * item.price;
      }, 0)
    : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isRtl ? `تفاصيل الطلب #${order.orderNumber}` : `Order Details #${order.orderNumber}`}
      icon={X}
      className="max-w-5xl w-full"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="space-y-6 max-h-[75vh] overflow-y-auto p-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: isRtl ? 'رقم الطلب' : 'Order Number', value: order.orderNumber },
            { label: isRtl ? 'الحالة' : 'Status', value: t(`orders.status_${order.status}`) },
            { label: isRtl ? 'تاريخ الطلب' : 'Order Date', value: order.date },
            { label: isRtl ? 'الفرع' : 'Branch', value: order.branch?.displayName || '—' },
          ].map((item, i) => (
            <div key={i} className="bg-white p-4 rounded-lg border border-gray-200 text-center">
              <p className="text-xs text-gray-600 font-medium">{item.label}</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Items Table */}
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-3">{isRtl ? 'قائمة المنتجات' : 'Product List'}</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-right text-gray-600 font-medium">{isRtl ? 'المنتج' : 'Product'}</th>
                  <th className="p-3 text-right text-gray-600 font-medium">{isRtl ? 'الكمية' : 'Quantity'}</th>
                  {isDelivered && (
                    <th className="p-3 text-right text-gray-600 font-medium">{isRtl ? 'التسليم' : 'Delivery'}</th>
                  )}
                  <th className="p-3 text-right text-gray-600 font-medium">{isRtl ? 'السعر' : 'Price'}</th>
                  <th className="p-3 text-right text-gray-600 font-medium">{isRtl ? 'الإجمالي' : 'Total'}</th>
                </tr>
              </thead>
              <tbody>
                {order.items
                  .filter(item => item.quantity > 0)
                  .map(item => {
                    const received = item.receivedQuantity || 0;
                    const isOver = received > item.quantity;
                    const isUnder = received < item.quantity;
                    const showArrow = isDelivered && !item.rejected && received !== item.quantity;

                    return (
                      <tr key={item.itemId} className={item.rejected ? 'bg-red-50' : 'border-b border-gray-100'}>
                        <td className="p-3 text-right font-medium text-gray-900">
                          {item.productName}
                          {item.rejected && <span className="text-red-600 mr-2">({t('orders.rejected')})</span>}
                        </td>
                        <td className="p-3 text-right text-gray-700">
                          {item.quantity} {item.unit}
                        </td>
                        {isDelivered && (
                          <td className="p-3 text-right">
                            {item.rejected ? (
                              <span className="text-red-600 font-medium">{t('orders.rejected')}</span>
                            ) : (
                              <div className="flex items-center justify-end gap-1.5">
                                <span>{item.quantity}</span>
                                <ArrowRight className="w-3.5 h-3.5 text-gray-500" />
                                <span className={isOver ? 'text-green-600 font-medium' : isUnder ? 'text-amber-600 font-medium' : 'text-gray-700'}>
                                  {received}
                                </span>
                                <span className="text-gray-500 text-xs ml-1">{item.unit}</span>
                              </div>
                            )}
                          </td>
                        )}
                        <td className="p-3 text-right text-gray-700">
                          {item.price.toLocaleString(isRtl ? 'ar-SA' : 'en-US')} {isRtl ? 'ر.س' : 'SAR'}
                        </td>
                        <td className="p-3 text-right font-medium">
                          {item.rejected ? (
                            <span className="text-red-500 line-through">0</span>
                          ) : (
                            <span className={isOver ? 'text-green-600' : isUnder ? 'text-amber-600' : 'text-gray-900'}>
                              {(received * item.price).toLocaleString(isRtl ? 'ar-SA' : 'en-US')}
                            </span>
                          )} {isRtl ? 'ر.س' : 'SAR'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Final Totals */}
        {isDelivered && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg border text-center">
              <p className="text-sm text-gray-600">{isRtl ? 'المطلوب' : 'Requested'}</p>
              <p className="text-lg font-bold text-gray-800">
                {originalTotal.toLocaleString(isRtl ? 'ar-SA' : 'en-US')} {isRtl ? 'ر.س' : 'SAR'}
              </p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 text-center">
              <p className="text-sm text-emerald-700 font-medium">{isRtl ? 'المستلم' : 'Received'}</p>
              <p className="text-xl font-bold text-emerald-600">
                {receivedTotal.toLocaleString(isRtl ? 'ar-SA' : 'en-US')} {isRtl ? 'ر.س' : 'SAR'}
              </p>
            </div>
            {adjustedTotal !== receivedTotal && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 text-center">
                <p className="text-sm text-amber-700 font-medium">{isRtl ? 'الإجمالي النهائي' : 'Final Total'}</p>
                <p className="text-lg font-bold text-amber-600">
                  {adjustedTotal.toLocaleString(isRtl ? 'ar-SA' : 'en-US')} {isRtl ? 'ر.س' : 'SAR'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {order.notes && (
          <div className="bg-gray-50 p-4 rounded-lg border">
            <p className="text-sm font-medium text-gray-700 mb-1">{isRtl ? 'ملاحظات' : 'Notes'}</p>
            <p className="text-sm text-gray-900">{order.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  );
});

ViewModal.displayName = 'ViewModal';
export default ViewModal;